"use client";

import LogoS from "@/assets/logo-s.svg";
import Logo from "@/assets/logo.svg";
import { useGlobalData } from "@/components/TinaContext";
import Section from "@/components/ui/Section";
import { Button } from "@/components/ui/button";
import { LinkIcon } from "lucide-react";
import Link from "next/link";
import {
  FaFacebook as FacebookIcon,
  FaGithub as GithubIcon,
  FaInstagram as InstagramIcon,
  FaLinkedin as LinkedInIcon,
  FaYoutube as YouTubeIcon,
} from "react-icons/fa";
import { FaXTwitter as XIcon } from "react-icons/fa6";

const PLATFORM_ICONS = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  x: XIcon,
  youtube: YouTubeIcon,
  github: GithubIcon,
  website: LinkIcon,
};

export default function Footer() {
  const { footer } = useGlobalData();
  const { tagline, taglineButton, columns, socials } = footer || {};

  return (
    <Section as="footer" wrapperClassName="bg-gray-700 text-white py-16 lg:pt-16 !pb-12">
      {/* Tagline bar */}
      {tagline && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-10  relative">
          <LogoS className="absolute -top-4 -left-4 h-20 w-auto text-white/10 -rotate-25" aria-label="Slán Docs Logo" />
          <p className="text-2xl lg:text-4xl font-medium z-10 relative">{tagline}</p>
          {taglineButton?.label && (
            <Button
              asChild
              // variant={taglineButton.variant || "default"}
              variant="white"
              size="lg"
              className="shrink-0"
              arrow={true}
            >
              <Link href={taglineButton.link || "/"}>{taglineButton.label}</Link>
            </Button>
          )}
        </div>
      )}

      {/* Link columns */}
      {columns?.length > 0 && (
        <div className="py-12 lg:py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {columns.map((col, index) => (
              <div key={col.title?.trim() || `column-${index}`}>
                <h3 className="font-semibold mb-4">{col.title}</h3>
                <ul className="flex flex-col gap-1">
                  {col.links?.map((link, index) => (
                    <li key={`${link.label ?? "link"}-${link.link ?? index}`}>
                      <Button asChild variant="link-white" className="text-base">
                        <Link href={link.link || "/"}>{link.label}</Link>
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom bar: logo + socials */}
      <div className="flex flex-col-reverse sm:flex-row items-baseline justify-between gap-6">
        <Link href="/" className="text-2xl font-bold">
          <Logo className="h-12 lg:h-24 w-auto translate-y-4" aria-label="Slán Docs home" />
        </Link>
        {socials?.length > 0 && (
          <nav aria-label="Social media">
            <ul className="flex items-center gap-4">
              {socials.map((social) => {
                const Icon = PLATFORM_ICONS[social.platform?.toLowerCase()];
                return (
                  <li key={social.platform?.toLowerCase() || social.url}>
                    <a
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.platform}
                      className="text-white/70 hover:text-white transition-colors"
                    >
                      {Icon ? <Icon className="h-6 w-6" /> : social.platform}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </div>
    </Section>
  );
}
